/**
 * scriptsRadio.js — Phrases types à dire à la radio pendant le guidage live.
 *
 * Indexé par code REMC (C1-a → C4-g). Chaque code a 3 niveaux :
 *  - FORT   : verbalisation COMPLÈTE (élève en découverte ou en difficulté)
 *  - MOYEN  : MOT-CLÉ seul (l'élève sait, on relance)
 *  - FAIBLE : signal ou silence (autonomie)
 *
 * Convention :
 *  - 2 à 4 phrases courtes par niveau (≤ 8 mots)
 *  - Adressées à la 2e personne (« regarde loin », « doses ton embrayage »)
 *  - Pas de formules vagues, des verbes d'action concrets
 *
 * Fallback : si un code n'est pas couvert ici, le composant SeanceLive
 * utilise un générique basé sur l'objectif court (objectifsConcis.js).
 */

export const SCRIPTS_RADIO = {
  /* ═══ C1 — HORS CIRCULATION (Plateau) ═══════════════════════════════ */
  'C1-e': {
    fort:   ['« Un… embraye »', '« Deux… gaz »', '« Trois… pars »', '« Tu freines, tu débraies »'],
    moyen:  ['« Patine »', '« Gaz »', '« Frein avant »', '« Pied gauche »'],
    faible: ['(signal de la main)', '(silence, je te regarde)'],
  },
  'C1-f': {
    fort:   ['« Tu accélères, tu débrayes, tu passes la 2 »', '« Ralenti, frein, rétrograde »', '« Garde le gaz dans la rétro »'],
    moyen:  ['« 2 »', '« Rétrograde »', '« Gaz dans la rétro »'],
    faible: ['(je regarde tes passages)'],
  },
  'C1-g': {
    fort:   ['« Avant 70 % d\'abord »', '« Arrière 30 % maintenant »', '« Tu maintiens le frein »', '« Débraye juste avant l\'arrêt »'],
    moyen:  ['« 70/30 »', '« Maintiens »', '« Débraye »'],
    faible: ['(je compte ton arrêt en mètres)'],
  },
  'C1-h': {
    fort:   ['« Regarde 2 cônes plus loin »', '« Tête tournée AVANT le virage »', '« Là où tu regardes, la moto va »'],
    moyen:  ['« Regard loin »', '« Tête »', '« Plus loin »'],
    faible: ['(je note ta trajectoire)'],
  },
  'C1-i': {
    fort:   ['« Pieds sur les repose-pieds »', '« Genoux serrés au réservoir »', '« Patine ton embrayage »', '« Tu regardes loin »'],
    moyen:  ['« Pieds »', '« Genoux »', '« Patine »'],
    faible: ['(silence, je te suis)'],
  },
  'C1-j': {
    fort:   ['« Trouve le point de patinage »', '« Doses, surtout doses »', '« Pas de claquement de levier »'],
    moyen:  ['« Doses »', '« Patine »'],
    faible: ['(je compte tes calages)'],
  },
  'C1-k': {
    fort:   ['« Pousse sur le guidon côté virage »', '« Trajectoire ext → int → ext »', '« Regarde la sortie »', '« N\'incline pas trop tôt »'],
    moyen:  ['« Pousse »', '« Sortie »', '« Trajectoire »'],
    faible: ['(je note ton point de corde)'],
  },
  'C1-l': {
    fort:   ['« Avant ET arrière en même temps »', '« Tu ne lâches pas »', '« Débraye en fin de freinage »'],
    moyen:  ['« Les deux freins »', '« Ne lâche pas »'],
    faible: ['(je chronomètre ton arrêt)'],
  },

  /* ═══ C2 — CIRCULATION NORMALE ══════════════════════════════════════ */
  'C2-a': {
    fort:   ['« Vitesse limite : 50, tu es à combien ? »', '« Stop = arrêt complet, pied au sol »', '« Ligne continue, pas de dépassement »'],
    moyen:  ['« Limite »', '« Stop »', '« Continue »'],
    faible: ['(j\'observe tes infractions)'],
  },
  'C2-b': {
    fort:   ['« Dis-moi à voix haute ce que tu vois »', '« Anticipe 5 secondes plus loin »', '« Que fait le piéton à droite ? »'],
    moyen:  ['« Anticipe »', '« Et lui ? »', '« Verbalise »'],
    faible: ['(je note tes oublis)'],
  },
  'C2-c': {
    fort:   ['« 1/3 droit hors agglo »', '« Centre ou 1/3 gauche en agglo »', '« Ne touche PAS la ligne médiane »', '« 1 mètre des voitures garées »'],
    moyen:  ['« Place-toi »', '« Pas la médiane »', '« Sécu »'],
    faible: ['(je note ton positionnement)'],
  },
  'C2-d': {
    fort:   ['« Ralentis AVANT le virage »', '« Tu es trop vite pour cet angle »', '« Adapte à la visibilité »'],
    moyen:  ['« Trop vite »', '« Ralentis »', '« Adapte »'],
    faible: ['(je note tes excès)'],
  },
  'C2-e': {
    fort:   ['« Aire visible, sol stable »', '« Béquille sur dur »', '« Rétro + angle mort avant de quitter »'],
    moyen:  ['« Visible »', '« Stable »', '« Rétro »'],
    faible: ['(je regarde ton emplacement)'],
  },
  'C2-f': {
    fort:   ['« Clignotant maintenant »', '« Rétro + angle mort »', '« Regarde où tu vas »', '« Pas de coupe d\'angle »'],
    moyen:  ['« Clignotant »', '« Angle mort »', '« Regard sortie »'],
    faible: ['(je compte tes oublis)'],
  },
  'C2-g': {
    fort:   ['« Tu vois le stop ? Arrêt complet »', '« Cédez = ralentis, prêt à stopper »', '« Priorité à droite ici »'],
    moyen:  ['« Stop »', '« Cède »', '« Droite »'],
    faible: ['(j\'observe ton anticipation)'],
  },
  'C2-h': {
    fort:   ['« Priorité aux engagés »', '« Clignotant droit APRÈS la sortie avant la tienne »', '« Regarde la sortie, pas l\'îlot »'],
    moyen:  ['« Cède »', '« Clignotant »', '« Regard sortie »'],
    faible: ['(je compte tes clignotants)'],
  },

  /* ═══ C3 — CONDITIONS DIFFICILES ════════════════════════════════════ */
  'C3-a': {
    fort:   ['« Compte 21 - 22 derrière lui »', '« Tu es trop près, recule »', '« Pluie = double la distance »'],
    moyen:  ['« 21-22 »', '« Recule »', '« Distance »'],
    faible: ['(je mesure ton intervalle)'],
  },
  'C3-b': {
    fort:   ['« Au moins 1 mètre en croisant »', '« Visibilité ? Temps ? Allons-y »', '« Rétro + angle mort AVANT et APRÈS »'],
    moyen:  ['« 1 mètre »', '« Visibilité »', '« Angle mort »'],
    faible: ['(je note tes dépassements)'],
  },
  'C3-c': {
    fort:   ['« Tout ton freinage AVANT le virage »', '« Trajectoire ext → int → ext »', '« Regarde la sortie »'],
    moyen:  ['« Freine avant »', '« Sortie »', '« Trajectoire »'],
    faible: ['(je note ta trajectoire)'],
  },
  'C3-d': {
    fort:   ['« Clignotant 3 secondes avant »', '« Coupe ton clignotant après »', '« Feux de détresse si tu freines fort »'],
    moyen:  ['« Clignotant »', '« Coupe »', '« Détresse »'],
    faible: ['(je note tes signaux)'],
  },
  'C3-e': {
    fort:   ['« Le vélo, 1,5 mètre minimum »', '« Ralentis devant les enfants »', '« Anticipe son écart »'],
    moyen:  ['« 1,5 m »', '« Doucement »', '« Anticipe »'],
    faible: ['(j\'observe ton respect)'],
  },
  'C3-f': {
    fort:   ['« Sors de l\'angle mort du camion »', '« Garde 50 mètres derrière »', '« Dépasse franc, sans hésiter »'],
    moyen:  ['« Angle mort »', '« 50 m »', '« Franc »'],
    faible: ['(je note tes positions)'],
  },
  'C3-g': {
    fort:   ['« Accélère à 90 avant d\'insérer »', '« Utilise toute la bande »', '« Clignotant + angle mort »'],
    moyen:  ['« 90 »', '« Bande »', '« Clignotant »'],
    faible: ['(je note ton insertion)'],
  },
  'C3-h': {
    fort:   ['« Centre voire 1/3 gauche en agglo »', '« Garde 1 seconde »', '« Pas de zigzag »'],
    moyen:  ['« Place »', '« 1 seconde »', '« Tiens »'],
    faible: ['(je note tes écarts)'],
  },
  'C3-i': {
    fort:   ['« Zone CIF autorisée ici »', '« 50 max, +20 par rapport à eux »', '« Pas de CIF dans le virage »'],
    moyen:  ['« CIF »', '« +20 »', '« Pas ici »'],
    faible: ['(j\'observe ton CIF)'],
  },
  'C3-j': {
    fort:   ['« Pluie : -20 % de vitesse »', '« Distance doublée »', '« Feux de croisement »'],
    moyen:  ['« -20 % »', '« Distance »', '« Feux »'],
    faible: ['(je note ton adaptation)'],
  },
  'C3-k': {
    fort:   ['« Feux dans le tunnel »', '« Visière relevée à l\'entrée »', '« Distance doublée, vent latéral »'],
    moyen:  ['« Feux »', '« Visière »', '« Distance »'],
    faible: ['(j\'observe ton passage)'],
  },

  /* ═══ C4 — AUTONOMIE ═══════════════════════════════════════════════ */
  'C4-a': {
    fort:   ['« Quelle direction prends-tu ? »', '« Repère le panneau »', '« Si tu te trompes, on recommence »'],
    moyen:  ['« Direction »', '« Panneau »', '« Repère »'],
    faible: ['(je te suis, choisis seul)'],
  },
  'C4-b': {
    fort:   ['« On fait une pause dans 30 min »', '« Hydrate-toi »', '« Vigilance, secoue-toi »'],
    moyen:  ['« Pause »', '« Eau »', '« Vigile »'],
    faible: ['(je surveille ta fatigue)'],
  },
  'C4-c': {
    fort:   ['« Zéro alcool au guidon »', '« Médicaments : as-tu vérifié ? »', '« Téléphone dans la poche »'],
    moyen:  ['« 0 g/L »', '« Médoc »', '« Téléphone »'],
    faible: ['(je note ton hygiène)'],
  },
  'C4-d': {
    fort:   ['« P-A-S : Protège, Alerte, Secourt »', '« 112, 15, 17, 18 »', '« Ne déplace pas le blessé »'],
    moyen:  ['« PAS »', '« 112 »', '« Pas toucher »'],
    faible: ['(scénario, à toi)'],
  },
  'C4-e': {
    fort:   ['« ABS = tu peux freiner fort »', '« Voyant allumé = en panne »', '« Sur gravillons : prudence quand même »'],
    moyen:  ['« ABS »', '« Voyant »', '« Limites »'],
    faible: ['(explique-moi le système)'],
  },
  'C4-f': {
    fort:   ['« Pneus à froid avant de partir »', '« Niveaux : huile, frein »', '« Chaîne tendue, lubrifiée »'],
    moyen:  ['« Pneus »', '« Niveaux »', '« Chaîne »'],
    faible: ['(check moi ça)'],
  },
  'C4-g': {
    fort:   ['« Régime entre 2000 et 4000 »', '« Anticipe, freine moins »', '« Coupe si arrêt long »'],
    moyen:  ['« Régime »', '« Anticipe »', '« Coupe »'],
    faible: ['(je note ta conso)'],
  },
};

/**
 * Renvoie le bloc de phrases pour un code REMC, ou un fallback générique.
 */
export function getScriptRadio(code, objectifCourt = '') {
  if (SCRIPTS_RADIO[code]) return SCRIPTS_RADIO[code];
  // Fallback générique basé sur l'objectif court
  return {
    fort:   [`« ${objectifCourt || 'Concentre-toi sur l\'objectif'} »`, '« Verbalise ce que tu fais »'],
    moyen:  ['« Concentre-toi »', '« Objectif »'],
    faible: ['(je note ton autonomie)'],
  };
}
